import React from "react";
import { Box, Button, Chip, InputAdornment, MenuItem, OutlinedInput, Select, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ListPageHeader, {
    ListPageHeaderGroupActions,
    ListPageHeaderRight,
} from "../../../../../Components/Misc/ListPageHeader";
import { dciService } from "../../../../../Core/Api/services";
import { GridActionsCellItem, GridColDef, GridRowParams, GridRowsProp } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../../../Components/Table/table";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { ModalContext } from "../../../../../Components/Modals";
import useCustomSnackbar from "../../../../../Utils/Hooks/useCustomSnackbar";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import TableSkeleton from "../../../../../Components/Table/TableSkeleton";
import { debounce } from "throttle-debounce";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import GlobalConst from "../../../../../Core/Utils/Constants/globals";
import {
    DbKdmRecipientInfos,
    KdmRecipientProjectorBrand,
    KdmRecipientType,
} from "../../../../../Core/Api/Types/db-kdm-recipient-infos";
import ModalKdmRecipient, {
    ModalKdmRecipientElementProps,
} from "../../../../../Components/Modals/ModalKdmRecipient/modal-kdm-recipient";
import CopyrightOutlinedIcon from "@mui/icons-material/CopyrightOutlined";
import TheaterComedyOutlinedIcon from "@mui/icons-material/TheaterComedyOutlined";
import { AclTypes } from "../../../../../Core/Api/Types/acl-types";
import { AclMatch, UserHasAcl, UserHasAcls, Distinct, True } from "../../../../../Utils/helpers";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import TableCellBool from "../../../../../Components/Table/TableCellBool";
import TableCell2Lines from "../../../../../Components/Table/TableCell2Lines/table-cell-2-lines";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../../Components/Table/table-uuids";

function InventoryKdm() {
    const { t } = useTranslation();
    const modalContext = React.useContext(ModalContext);
    const { openModal } = { ...modalContext };
    // data
    const [kdmRecipients, setKdmRecipients] = React.useState<DbKdmRecipientInfos[]>([]);
    // search
    const [filterText, setFilterText] = React.useState<string>("");
    // filters
    const [filterType, setFilterType] = React.useState<KdmRecipientType | undefined>(undefined);
    const [theaterFilter, setTheaterFilter] = React.useState<string>("");
    const [brandFilter, setBrandFilter] = React.useState<KdmRecipientProjectorBrand | undefined>(undefined);
    const enqueueSnackbar = useCustomSnackbar();
    const [isLoading, setIsLoading] = React.useState(true);

    const apiGrid = useGridApiRef();

    // Main load
    React.useEffect(() => {
        dciService.ListKdmRecipients().then(function (response) {
            if (response.success) setKdmRecipients(response.recipients || []);
            setIsLoading(false);
        });
    }, []);

    // Table creation
    const rows: GridRowsProp = kdmRecipients;
    type Row = (typeof rows)[number];
    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: "Id",
            minWidth: CellSize.xsmall,
            flex: CellSize.xsmall,
        },
        {
            field: "name",
            headerName: t("common:title"),
            minWidth: CellSize.small,
            flex: CellSize.small,
            valueGetter: (value, row: Row, column, apiRef) => {
                if (!row) return;
                // if (row.type === "group") return apiRef.getRow(row.children?.find(True))?.name;
                return value;
            },
            renderCell: (data: Row) => data.value,
        },
        {
            field: "address",
            headerName: t("common:address"),
            minWidth: CellSize.large,
            flex: CellSize.large,
            valueGetter: (value, row: Row, column, apiRef) => {
                if (!row) return;
                if (row.type === "group") {
                    const firstRow = row.children[0];
                    return `${firstRow.address} ${firstRow.zipCode} ${firstRow.city} ${firstRow.region} ${firstRow.country}`;
                }
                return `${row.address} ${row.zipCode} ${row.city} ${row.region} ${row.country}`;
            },
            renderCell: (data: Row) => {
                return (
                    <TableCell2Lines
                        desc={`${data?.row?.address} ${data?.row?.zipCode}`}
                        title={`${data?.row?.city ?? ""} ${data?.row?.region ? ` (${data?.row?.region}), ` : ""} ${data?.row?.country
                            }`}
                    />
                );
            },
        },
        {
            field: "roomName",
            headerName: t("common:room"),
            minWidth: CellSize.xsmall,
            flex: CellSize.xsmall,
            renderCell: (data: Row) => {
                if (data.rowNode.type === "group") {
                    return data.value;
                }
                return (
                    <Tooltip title={data.value}>
                        <Chip sx={{ wordBreak: "break-all", wordWrap: "break-word" }} label={data.value ?? "N/A"} />
                    </Tooltip>
                );
            },
        },
        // {
        //   field: "type",
        //   headerName: "Type",
        //   minWidth: 140,
        //   flex: 140,
        //   renderCell: (data: Row) => {
        //     if (data.rowNode.type === "group") {
        //       return data.value;
        //     }
        //     return <Chip sx={{ wordBreak: "break-all", wordWrap: "break-word" }} label={data.value} />;
        //   },
        // },
        {
            field: "theater",
            headerName: t("common:theater"),
            minWidth: CellSize.medium,
            flex: CellSize.medium,
            renderCell: (data: Row) => {
                if (data.rowNode.type === "group") {
                    return data.value;
                }
                return (
                    <Typography variant="body2" sx={{ wordBreak: "break-all", wordWrap: "break-word" }}>
                        {data.value}
                    </Typography>
                );
            },
        },
        {
            field: "contact",
            headerName: t("common:contact"),
            minWidth: CellSize.large,
            flex: CellSize.large,
            valueGetter: (value, row: Row) => {
                if (!row) return;
                if (row.type === "group") return null;
                return `${row?.contact?.fullName} ${row?.contact?.email} ${row?.contact?.phone}`;
            },
            renderCell: (data: Row) => {
                console.log(data)
                if (!data.row.contact) return null;
                return (
                    <TableCell2Lines
                        title={`${data.row.contact?.fullName}`}
                        desc={`${data.row.contact?.email} ${data.row.contact?.phone ? ` (${data.row.contact?.phone})` : ``}`}
                    />
                );
            },
        },
        {
            field: "cert",
            headerName: t("table:headerName.certificate"),
            minWidth: CellSize.small,
            flex: CellSize.small,
            valueGetter: (value, row: Row) => row?.cert !== null,
            renderCell: (data: Row) => {
                if (data.rowNode.type === "group") {
                    let valideCert = data.rowNode.children.reduce(
                        (sum, id) => sum + (data.api.getRow(id).cert !== null ? 1 : 0),
                        0
                    );

                    return (
                        <>
                            <Typography variant="body2" sx={{ wordBreak: "break-all", wordWrap: "break-word" }}>
                                {valideCert}/{data.rowNode.children.length}
                            </Typography>
                            <TableCellBool value={valideCert === data.rowNode.children.length} />
                        </>
                    );
                }

                return <TableCellBool value={data.value} />;
            },
        },
        {
            field: "globecast",
            headerName: t("table:headerName.globecase"),
            minWidth: CellSize.medium,
            flex: CellSize.medium,
            valueGetter: (data: any, row: any) => row?.globecast !== null,
            renderCell: (data: Row) => {
                if (data.rowNode.type === "group") {
                    return data.value;
                }

                return data?.row?.globecast !== null ? (
                    <TableCell2Lines
                        title={t("table:cellTitle.siteWithId", { id: data?.row?.globecast.siteId })}
                        desc={
                            data?.row?.globecast.theaterId
                                ? t("table:cellDesc.theaterWithId", { id: data?.row?.globecast.theaterId })
                                : t("common:configured")
                        }
                    />
                ) : (
                    "N/A"
                );
            },
        },
        /*{
          field: "creationInfos",
          headerName: "Creation",
          flex: 150,
          valueGetter: (data: any, row: any) => row?.creationInfos?.date,
          renderCell: (data) => (
            <>
              {!data?.row?.creationInfos ? (
                <Typography>_</Typography>
              ) : (
                <TableCellOwner2Lines
                  userName={data.row["createdByUserName"]}
                  groupName={moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
                />
              )}
            </>
          ),
        },*/
        {
            field: "actions",
            headerName: "",
            type: "actions",
            flex: CellSize.actionsSmall,
            minWidth: CellSize.actionsSmall,
            align: "right",
            getActions: (params: GridRowParams) => {
                if (!params || !params.row) return [];
                if (params?.row?.id === undefined) {
                    return [];
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon sx={{ width: 20, height: 20 }} />}
                        label={t("common:editUpperCase")}
                        onClick={() => openRecipientModal(kdmRecipients.find((x) => x.id === params?.row?.id))}
                        disabled={!UserHasAcl(AclTypes.aclKdmRecipientAdmin)}
                        showInMenu
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon sx={{ width: 20, height: 20 }} />}
                        label={t("common:delete")}
                        onClick={() => handleDelete(params?.row?.id)}
                        disabled={!UserHasAcl(AclTypes.aclKdmRecipientAdmin)}
                        showInMenu
                    />,
                ];
            },
        },
    ];

    const handleDelete = (id: number) => {
        dciService.DeleteKdmRecipient(id).then(function (response) {
            if (response.success) {
                setKdmRecipients(kdmRecipients.filter((x) => x.id !== id));
                enqueueSnackbar(t("alert:success.kdmRecipientDeleted"), "success");
            }
        });
    };

    const openRecipientModal = (recipient?: DbKdmRecipientInfos) => {
        if (!openModal) return;
        openModal<ModalKdmRecipientElementProps>(ModalKdmRecipient, {
            recipient,
            recipients: kdmRecipients,
            setRecipients: setKdmRecipients,
        });
    };

    const handleSyncKdms = () => {
        // handle sync kdms
        dciService.SyncKdmRecipients().then(function (response) {
            if (response.success) {
                enqueueSnackbar(t("alert:success.kdmRecipientsSynchronizationCalled"), "success");
            }
        });
    };

    // Filter
    const updateSearchValue = React.useCallback((newSearchValue: string) => {
        setFilterText(newSearchValue);
    }, []);

    const debouncedUpdateSearchValue = React.useMemo(() => debounce(500, updateSearchValue), [updateSearchValue]);

    const handleSearchValueChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newSearchValue = event.target.value;
            debouncedUpdateSearchValue(newSearchValue);
        },
        [debouncedUpdateSearchValue]
    );

    const additionalFilters = (
        <>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                startAdornment={<LightbulbOutlinedIcon />}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={filterType}
                onChange={(e) => setFilterType(KdmRecipientType[e.target.value])}
                disabled={isLoading}
            >
                <MenuItem value={undefined}>{t("common:type")}</MenuItem>
                {Object.keys(KdmRecipientType).map((type) => (
                    <MenuItem value={type} key={type}>
                        {type}
                    </MenuItem>
                ))}
            </Select>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                startAdornment={<TheaterComedyOutlinedIcon />}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={theaterFilter}
                onChange={(e) => setTheaterFilter(e.target.value)}
                disabled={isLoading}
            >
                <MenuItem value={""}>{t("field:label.theaterCompany")}</MenuItem>
                {kdmRecipients
                    .map((rec) => rec.theater)
                    .filter((rec) => rec !== "" && rec !== null)
                    .filter(Distinct)
                    .map((theater) => (
                        <MenuItem value={theater!} key={theater!}>
                            {theater}
                        </MenuItem>
                    ))}
            </Select>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                startAdornment={<CopyrightOutlinedIcon />}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={brandFilter}
                onChange={(e) => setBrandFilter(KdmRecipientProjectorBrand[e.target.value])}
                disabled={isLoading}
            >
                <MenuItem value={undefined}>{t("menu:item.projectorBrand")}</MenuItem>
                {Object.keys(KdmRecipientProjectorBrand).map((brand) => (
                    <MenuItem value={brand} key={brand}>
                        {brand}
                    </MenuItem>
                ))}
            </Select></>
    )

    const additionalActions = (
        <>
            <Button
                startIcon={<SyncOutlinedIcon />}
                color="primary"
                variant="outlined"
                onClick={() => handleSyncKdms()}
                disabled={isLoading || !UserHasAcls([AclTypes.aclKdmRecipientAdmin, AclTypes.aclSuperUser], AclMatch.All)}
            >
                {t("common:sync")}
            </Button>
            <Button
                startIcon={<AddIcon />}
                color="primary"
                variant="contained"
                onClick={() => openRecipientModal()}
                disabled={isLoading || !UserHasAcl(AclTypes.aclKdmRecipientAdmin)}
            >
                {t("modal:title.newRecipient")}
            </Button></>
    )

    return (
        <DataTable
            loading={isLoading}
            tableKey={TableUuids.INVENTORY_KDM_RECIPIENTS}
            additionalFilters={additionalFilters}
            additionalActions={additionalActions}
            rows={kdmRecipients.filter(
                (x) =>
                    (!filterType || x.type === filterType) &&
                    (theaterFilter === "" || x.theater === theaterFilter) &&
                    (!brandFilter || x.projector?.brand === brandFilter)
            )}
            columns={columns}
            disableRowSelectionOnClick
            getRowHeight={() => "auto"}
            apiRef={apiGrid}
        />
    );
}

export default InventoryKdm;
